import { createAsyncThunk } from "@reduxjs/toolkit"
import type { AxiosProgressEvent } from "axios"
import type { NavigateFunction } from "react-router/dist/lib/hooks"

import { RoutesPaths } from "../../../constants/routes.ts"
import { errorSave } from "../../../redux/errors.slice"
import type { RootState } from "../../../store.ts"
import { unknownError } from "../../../utils/handlers/handle-error"
import i18n from "../../../i18n.ts"
import { showResponseMessage } from "../../../utils/handlers/show-response-message.ts"
import type { LeadContract } from "../../Lead/types/lead.documents.types.ts"
import * as proposalServices from "../../Proposals/api/services"
import { type Proposal } from "../../Proposals/types/types.ts"
import { type ContractFormData } from "../ContractAddPage/components/contract-form-data.tsx"
import * as services from "../api/services"
import { type Contract, type ContractListItem } from "../types/types.ts"
import { contractsSliceName } from "./contracts.constants.ts"
import { type ContractRequestData } from "./contracts.thunks.types.ts"
import type {
  ContractFilterOptions,
  ContractFilters
} from "./contracts.types.ts"

export const fetchContracts = createAsyncThunk<
  ContractListItem[],
  ContractFilters | undefined,
  { state: RootState }
>(
  `${contractsSliceName}/fetchContracts`,
  async (filters, { dispatch, getState, rejectWithValue }) => {
    const params = filters || getState()?.proposals?.filters
    const response = await services.getContracts(params)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response?.data || []
  }
)

export const fetchContract = createAsyncThunk<Contract, string>(
  `${contractsSliceName}/fetchContract`,
  async (id, { dispatch, rejectWithValue }) => {
    const response = await services.getContractById(id)

    if (!response.ok) {
      dispatch(errorSave((response.data as any)?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response.data!
  }
)

export const fetchContractInitialData = createAsyncThunk<
  ContractFormData,
  string
>(
  `${contractsSliceName}/fetchContractInitialData`,
  async (leadID, { dispatch, rejectWithValue }) => {
    const response = await services.getContractInitialValues(leadID)

    if (!response.ok) {
      dispatch(errorSave(response?.data?.details))
      return rejectWithValue(response.data || { errors: "Unknown error" })
    }

    return response.data! as unknown as ContractFormData
  }
)

export const getContractPreview = createAsyncThunk<string, ContractRequestData>(
  `${contractsSliceName}/previewContract`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.previewContract(params)

    if (!response.ok) {
      dispatch(errorSave(unknownError))
      return rejectWithValue(null)
    }

    return response.data!
  }
)

export const createContract = createAsyncThunk<string, ContractRequestData>(
  `${contractsSliceName}/createContract`,
  async (data, { dispatch, rejectWithValue }) => {
    const res = await services.createContract(data)

    if (!res.ok) {
      dispatch(errorSave(unknownError))
      return rejectWithValue("")
    }
    const contractId = res?.data?.details?.data?.id

    if (typeof contractId !== "string") {
      dispatch(errorSave(unknownError))
      return rejectWithValue("Invalid contract ID")
    }

    return contractId
  }
)

export const createContractFromProposal = createAsyncThunk<
  void,
  { lead_id: string; proposal_id: string }
>(
  `${contractsSliceName}/createContractFromProposal`,
  async ({ proposal_id, lead_id }, { dispatch, rejectWithValue }) => {
    const proposalRes = await proposalServices.getProposalById(proposal_id)

    if (!proposalRes?.ok) {
      dispatch(errorSave(proposalRes?.data?.details || unknownError))
      return rejectWithValue(null)
    }

    const proposal = proposalRes?.data as Proposal

    const uploadedAttachment = proposal?.attachments.find(
      (attachment) => attachment?.extra?.uploaded
    )
    const fileUrl = uploadedAttachment?.file_url

    if (!fileUrl) {
      dispatch(errorSave("No uploaded attachment found in proposal."))
      return rejectWithValue(null)
    }

    const fileRes = await fetch(fileUrl, { cache: "no-store" })
    if (!fileRes.ok) {
      dispatch(errorSave("Failed to fetch uploaded attachment."))
      return rejectWithValue(null)
    }

    const blob = await fileRes.blob()
    const fileName = uploadedAttachment?.file_name || "contract-source-file.pdf"
    const file = new File([blob], fileName, {
      type: blob.type || "application/pdf"
    })

    await dispatch(uploadContract({ file, lead_id })).unwrap()
  }
)

export const generateContract = createAsyncThunk<
  any,
  {
    data: ContractRequestData
    navigate: NavigateFunction
    leadId: string
    contractId: string
  }
>(
  `${contractsSliceName}/generateContract`,
  async (
    { navigate, leadId, data, contractId },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const generateRes = await services.updateContract(contractId, data)
      if (!generateRes.ok) throw new Error()

      const saveRes = await services.saveContractFile(contractId)
      if (!saveRes.ok) throw new Error()

      showResponseMessage({}, "Contract created successfully.")
      navigate(RoutesPaths.lead.view(leadId))

      return saveRes?.data
    } catch (e: unknown) {
      dispatch(errorSave(unknownError))
      return rejectWithValue(null)
    }
  }
)

export const sendContractToApproval = createAsyncThunk<
  void,
  {
    data: ContractRequestData
    navigate: NavigateFunction
    leadId: string
    contractId: string
  },
  { state: RootState }
>(
  `${contractsSliceName}/sendToApproval`,
  async (
    { navigate, leadId, data, contractId },
    { dispatch, rejectWithValue }
  ) => {
    try {
      const updateRes = await services.updateContract(contractId, data)
      if (!updateRes.ok) throw new Error()

      const saveRes = await services.saveContractFile(contractId)
      if (!saveRes.ok) {
        dispatch(errorSave((saveRes.data as { details?: unknown })?.details))
        return rejectWithValue(null)
      }

      const attachmentId = (
        saveRes.data as unknown as { details?: { data?: { id?: string } } }
      )?.details?.data?.id
      if (!attachmentId) throw new Error()

      const submitRes = await services.submitForReview({
        contractId,
        attachmentId
      })
      if (!submitRes.ok) {
        dispatch(errorSave((submitRes.data as { details?: unknown })?.details))
        return rejectWithValue(null)
      }

      showResponseMessage({}, i18n.t("documentApproval.contractSent"))
      navigate(RoutesPaths.lead.view(leadId))
    } catch (e: unknown) {
      dispatch(errorSave(unknownError))
      return rejectWithValue(null)
    }
  }
)

export const saveContract = createAsyncThunk<
  Contract,
  { data: any; navigate: NavigateFunction }
>(
  `${contractsSliceName}/saveContract`,
  async ({ navigate, data }, { dispatch, rejectWithValue }) => {
    const response = await services.createContract(data)

    if (!response.ok) {
      dispatch(errorSave(unknownError))
      return rejectWithValue(null)
    }

    showResponseMessage(response, "Contract created successfully.")
    navigate(RoutesPaths.lead.view(data?.lead_id))

    return response!.data!.details.data
  }
)

export const updateContract = createAsyncThunk<
  void,
  {
    data: ContractRequestData
    contractId: string
    showSuccessMessage?: boolean
  }
>(
  `${contractsSliceName}/updateContract`,
  async (
    { contractId, data, showSuccessMessage },
    { dispatch, rejectWithValue }
  ) => {
    const response = await services.updateContract(contractId, data)

    if (!response.ok) {
      dispatch(errorSave(unknownError))
      return rejectWithValue(null)
    }

    if (showSuccessMessage) {
      showResponseMessage(response, "Contract updated successfully.")
    }
  }
)

export const submitContractForReview = createAsyncThunk<
  LeadContract,
  {
    contractId: string
    attachmentId: string
  }
>(
  `${contractsSliceName}/submitForReview`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.submitForReview(data)

    if (!response.ok) {
      dispatch(errorSave(unknownError))
      return rejectWithValue(null)
    }

    showResponseMessage(response, "Contract submitted for review successfully.")

    return response!.data!.details.data
  }
)

export const rejectContract = createAsyncThunk<
  null,
  { id: string; reason: string }
>(
  `${contractsSliceName}/rejectContract`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.rejectContract(params)

    if (!response.ok) {
      dispatch(errorSave(unknownError))
      return rejectWithValue(null)
    }
    showResponseMessage(response, "Contract rejected successfully.")

    return null
  }
)

export const cancelContract = createAsyncThunk<
  null,
  { id: string; reason: string; notes: string }
>(
  `${contractsSliceName}/cancelContract`,
  async (params, { dispatch, rejectWithValue }) => {
    const response = await services.cancelContract(params)

    if (!response.ok) {
      dispatch(errorSave(unknownError))
      return rejectWithValue(null)
    }

    showResponseMessage(response, "Contract cancelled successfully.")

    return null
  }
)

export const approveContract = createAsyncThunk<null, string>(
  `${contractsSliceName}/approveContract`,
  async (contractId, { dispatch, rejectWithValue }) => {
    const response = await services.approveContract(contractId)

    if (!response.ok) {
      dispatch(errorSave(unknownError))
      return rejectWithValue(null)
    }

    showResponseMessage(response, "Contract approved successfully.")

    return null
  }
)

export const duplicateContract = createAsyncThunk<LeadContract, string>(
  `${contractsSliceName}/duplicateContract`,
  async (data, { dispatch, rejectWithValue }) => {
    const response = await services.duplicateContract(data)

    if (!response.ok) {
      dispatch(errorSave(unknownError))
      return rejectWithValue(null)
    }

    showResponseMessage({}, "Contract duplicated successfully.")

    return response!.data!.details.data
  }
)

export const uploadContract = createAsyncThunk<
  LeadContract,
  {
    file: File
    lead_id: string
    onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
  }
>(
  `${contractsSliceName}/uploadContract`,
  async ({ onUploadProgress, ...data }, { dispatch, rejectWithValue }) => {
    const response = await services.uploadContract(data, { onUploadProgress })

    if (!response.ok) {
      dispatch(errorSave(unknownError))
      return rejectWithValue(null)
    }

    showResponseMessage(response, "Contract uploaded successfully.")

    return response!.data!.details.data
  }
)

export const markContractAsSent = createAsyncThunk<LeadContract, string>(
  `${contractsSliceName}/markContractAsSent`,
  async (contractId, { dispatch, rejectWithValue }) => {
    const response = await services.sentContractToClient(contractId)

    if (!response.ok) {
      dispatch(errorSave(unknownError))
      return rejectWithValue(null)
    }

    showResponseMessage(response, "Contract marked as sent successfully.")

    return response!.data!.details.data
  }
)

export const emailContractToClient = createAsyncThunk<LeadContract, string>(
  `${contractsSliceName}/emailContractToClient`,
  async (contractId, { dispatch, rejectWithValue }) => {
    const response = await services.sendContractEmail(contractId)

    if (!response.ok) {
      dispatch(
        errorSave((response.data as { error?: string })?.error || unknownError)
      )
      return rejectWithValue(null)
    }

    showResponseMessage(response, "Contract emailed successfully.")

    return response!.data!.details.data
  }
)

export const getContractFilterOptions = createAsyncThunk<
  ContractFilterOptions,
  void
>(
  `${contractsSliceName}/getFilterOptions`,
  async (_, { dispatch, rejectWithValue }) => {
    const response = await services.getFilterOptions()

    if (!response.ok) {
      dispatch(errorSave(unknownError))
      return rejectWithValue(null)
    }

    return response.data!
  }
)
